/**
 * Campos y firma de configuración para el asistente cuando dataSource = custom_query (QueryBuilder).
 * Mantiene name = clave lógica del builder (p. ej. municipios.nombre, SUM(...)); label = alias legible.
 */

import type { ComponentConfig } from '../types/builder.types'

export type BuilderFieldOption = {
  name: string
  label: string
  type: 'string' | 'number'
  aggregatable: boolean
}

function inferFieldType(field: string, label: string): 'string' | 'number' {
  const baseName = field.includes('.') ? field.split('.').pop()! : field
  const lowerBase = baseName.toLowerCase()
  const lowerLabel = label.toLowerCase()
  // No usar el path completo (p. ej. valoraciones_economicas.*) porque contiene "valor"
  // en el nombre de tabla y dispara falsos positivos frente al indicador "valor".
  const haystack = `${lowerBase} ${lowerLabel}`
  if (/\d{4}/.test(baseName) || /^v_[a-z0-9_]+\d{4}$/i.test(baseName)) return 'number'
  if (/^v_/.test(haystack) && /\d+/.test(haystack)) return 'number'
  const numberIndicators = [
    'población', 'cantidad', 'total', 'suma', 'promedio', 'valor',
    'precio', 'costo', 'superficie', 'área', 'hectáreas', 'volumen',
    'peso', 'altura', 'ancho', 'largo', 'densidad', 'porcentaje',
    'ratio', 'índice', 'número', 'nº', 'nro', 'nitr', 'nitratos',
    'pies', 'basal', 'corteza', 'biomasa', 'carbono', 'incremento',
  ]
  const stringIndicators = [
    'nombre', 'municipio', 'provincia', 'localidad', 'dirección',
    'descripción', 'especie', 'común', 'codigo_especie',
    'tipo', 'email', 'calle', 'latitud', 'longitud',
  ]
  for (const indicator of numberIndicators) {
    if (haystack.includes(indicator)) return 'number'
  }
  for (const indicator of stringIndicators) {
    if (haystack.includes(indicator)) return 'string'
  }
  if (field.includes('(') && field.includes(')')) return 'number'
  return 'string'
}

/**
 * Firma estable para dependencias de useMemo cuando cambia la consulta del QueryBuilder.
 */
export function getCustomQueryConfigSignature(
  cq: ComponentConfig['customQuery'] | undefined
): string {
  if (!cq?.config?.selectedFields?.length) return ''
  return JSON.stringify({
    sf: cq.config.selectedFields,
    fa: cq.config.fieldAliases ?? {},
    ce: cq.config.computedColumnExpressions ?? {},
    agg: cq.config.agreggation ?? [],
    sql: cq.sql ?? '',
  })
}

/**
 * Lista de campos para selects del asistente; `name` coincide con selectedFields / chartConfig.
 */
export function fieldsFromCustomQuery(
  cq: ComponentConfig['customQuery'] | undefined
): BuilderFieldOption[] {
  if (!cq?.config?.selectedFields?.length) return []
  const aliases = (cq.config.fieldAliases || {}) as Record<string, string>
  return cq.config.selectedFields.map((field) => {
    const alias = aliases[field]
    const label =
      alias ||
      (field.includes('.') ? field.split('.').pop()! : field)
    const type = inferFieldType(field, label)
    return {
      name: field,
      label,
      type,
      aggregatable: type === 'number',
    }
  })
}

/**
 * Normaliza el valor devuelto por executeComponentQuery / RPC para vista previa y dashboard.
 */
export function normalizeQueryResultData(
  result: unknown,
  componentType: string
): unknown {
  if (result == null) {
    return componentType === 'kpi' ? 0 : []
  }

  if (componentType === 'kpi') {
    if (typeof result === 'number' && !Number.isNaN(result)) return result
    if (typeof result === 'bigint') return Number(result)
    if (typeof result === 'object' && result !== null && 'value' in result) {
      const v = (result as { value: unknown }).value
      if (typeof v === 'number') return v
      if (typeof v === 'string' && v !== '') {
        const n = Number(v)
        return Number.isNaN(n) ? 0 : n
      }
    }
    if (Array.isArray(result) && result.length > 0) {
      const row = result[0] as Record<string, unknown>
      const num = Object.values(row).find((v) => typeof v === 'number')
      if (typeof num === 'number') return num
    }
    if (
      typeof result === 'object' &&
      result !== null &&
      'data' in result &&
      Array.isArray((result as { data: unknown }).data) &&
      (result as { data: unknown[] }).data.length > 0
    ) {
      const row = (result as { data: Record<string, unknown>[] }).data[0]
      const num = Object.values(row).find((v) => typeof v === 'number')
      if (typeof num === 'number') return num
    }
    return 0
  }

  if (Array.isArray(result)) return result

  if (
    typeof result === 'object' &&
    result !== null &&
    'data' in result &&
    Array.isArray((result as { data: unknown }).data)
  ) {
    return (result as { data: unknown[] }).data
  }

  return []
}
